import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Figma "Button" (ver.6): Primary = #FD5108 / 700 white, Secondary = white + 1px #CBD1D6 / 500, Size = Md(44) · Sm(38).
// Radius: 10 (Md, secondary Sm) · 8 (primary Sm). Secondary's 1px border makes it 2px taller than primary.
const nvButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-nv-control transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-nv-brand font-bold text-nv-text-inverse hover:bg-nv-brand",
        secondary: "border border-nv-border-strong bg-nv-surface text-nv-text-primary hover:bg-nv-bg-subtle",
        ghost: "text-nv-brand hover:bg-nv-brand-subtle",
      },
      size: {
        md: "px-4 py-[10px] text-nv-button-md",
        sm: "px-4 py-2 text-nv-button-sm",
      },
    },
    compoundVariants: [{ variant: "primary", size: "sm", className: "rounded-nv-chip" }],
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface NvButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof nvButtonVariants> {
  asChild?: boolean;
}

const NvButton = React.forwardRef<HTMLButtonElement, NvButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(nvButtonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
NvButton.displayName = "NvButton";

export { NvButton, nvButtonVariants };
